import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../commons/Auth';
import { useAlert } from 'react-alert';
import { useLoading } from '../../commons/Loading';
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import {
  sailInvoicesPanoramicaList,
  saveCachedPanoramica,
} from '../../lib/fetch/sailInvoicesPanoramica';
import SailInvoicesPresentational from './SailInvoicesPresentational';
import { list } from '../../lib/fetch/profiles';
import { createPanoramica } from '../../lib/fetch/sailInvoices';
import './SailInvoices.scss';

const { REACT_APP_DEFAULT_PAGINATION_LIMIT = 50 } = process.env;

function SailInvoicesPanoramica(props) {
  const { t } = useTranslation('SAIL_INVOICE_PANORAMICA');
  const [{ newToken } = {}] = useAuth();
  const alert = useAlert();
  const { showLoading, hideLoading } = useLoading();

  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [subjects, setSubjects] = useState([]);

  const [query, setQuery] = useQueryParams({
    year: withDefault(NumberParam, new Date().getFullYear()),
    offset: withDefault(NumberParam, 0),
    customer: withDefault(StringParam, ''),
    search: withDefault(StringParam, ''),
    paid: withDefault(StringParam, ''),
    limit: withDefault(NumberParam, +REACT_APP_DEFAULT_PAGINATION_LIMIT),
  });

  const { year, offset, limit, customer, search, paid } = query;

  function onFilter(queryDiff) {
    setQuery(queryDiff);
  }

  const fetchSailInvoicesPanoramica = async () => {
    showLoading();
    const { error, data, headers } = await sailInvoicesPanoramicaList({
      newToken,
      year,
      search,
      offset,
      limit,
      customer,
      paid,
    });
    if (error) {
      setData([]);
      alert.error(t('COMMON:GENERIC_ERROR'));
      hideLoading();
      return;
    }

    setData([...data]);
    setTotal(Number(headers['x-total-count']));
    hideLoading();
  };

  const onCollectInvoice = async ({ invoice }) => {
    showLoading();

    const sailInvoice = {
      sailInvoice: {
        collected: invoice.sailInvoice?.collected || false,
        collectedDate: invoice.sailInvoice?.collectedDate || null,
        gross: Number(Number(invoice.sailInvoice?.gross).toFixed(2)) || 0,
        type: invoice.sailInvoice?.type || '',
        notPaid: Number(invoice.sailInvoice?.notPaid.toFixed(2)) || 0,
      },
    };

    const { error, data, headers } = await saveCachedPanoramica({
      data: sailInvoice,
      newToken,
      _id: invoice._id,
    });
    if (error) {
      setData([]);
      alert.error(t('COMMON:GENERIC_ERROR'));
      hideLoading();
      return;
    }

    alert.success(t('COMMON:SUCCESS_UPDATE'));
    fetchSailInvoicesPanoramica();
  };

  const onSendInvoice = async (invoice) => {
    showLoading();
    const { error, data } = await createPanoramica({
      newToken,
      data: invoice,
    });
    if (error) {
      alert.error(t('COMMON:GENERIC_ERROR'));
      hideLoading();
      return;
    }

    hideLoading();
    fetchSailInvoicesPanoramica();
  };

  useEffect(() => {
    fetchSailInvoicesPanoramica();
  }, [year, offset, limit, customer, search, paid]);

  useEffect(() => {
    const fetchProfiles = async () => {
      showLoading();
      const { error, data } = await list({
        newToken,
        limit: 200,
      });
      if (error) {
        setSubjects([]);
        return;
      }
      setSubjects(data);
    };

    fetchProfiles();
  }, []);

  return (
    <SailInvoicesPresentational
      data={data}
      total={total}
      query={query}
      onFilter={onFilter}
      onCollectInvoice={onCollectInvoice}
      onStatusChanged={() => fetchSailInvoicesPanoramica()}
      subjects={subjects}
      onSendInvoice={onSendInvoice}
      {...props}
    />
  );
}

export default SailInvoicesPanoramica;
